import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { ClockIcon, XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import RichTextDisplay from './RichTextDisplay';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { sanitizeHtml, stripHtml } from '../utils/sanitize';

interface Activity {
  id: string;
  activityType: string;
  previousState?: string;
  newState?: string;
  metadata?: any;
  performedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ActivityTabProps {
  entityId: string;
  onTabChange?: (tab: string) => void; // Callback to switch tabs without URL navigation
}

interface PreviewFile {
  filename: string;
  mimeType: string;
  storagePath: string;
  size: number;
  isImage: boolean;
  isPdf: boolean;
  isWord?: boolean;
  isExcel?: boolean;
  isCsv?: boolean;
  attachmentId?: string;
}

// Helper functions for file type detection
const isOfficeDocument = (mimeType: string): boolean => {
  const officeTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
    'application/msword', // DOC
    'application/vnd.ms-excel', // XLS
  ];
  return officeTypes.includes(mimeType);
};

const isWordDocument = (mimeType: string): boolean => {
  return mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword' ||
    mimeType?.includes('word');
};

const isExcelDocument = (mimeType: string): boolean => {
  return mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType?.includes('spreadsheet') ||
    mimeType?.includes('excel');
};

const isCsvFile = (mimeType: string, filename: string): boolean => {
  return mimeType === 'text/csv' || filename?.toLowerCase().endsWith('.csv');
};

const ActivityTab: React.FC<ActivityTabProps> = ({ entityId, onTabChange }) => {
  const { formatDateTime, formatDate } = useDateFormatter();
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['case-activities', entityId],
    queryFn: async () => {
      const response = await api.get(`/cases/${entityId}/activities`);
      return response.data as Activity[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-12">
        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">No activities recorded</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-base font-medium text-gray-900">Activity History</h3>
      </div>
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                Date/Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                Activity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {activities.map((activity: any) => {
              // Format activity description based on type
              const getActivityDescription = () => {
                if (activity.activityType === 'state_change') {
                  return 'State Changed';
                } else if (activity.activityType === 'bulk_update') {
                  return 'Bulk Update';
                } else if (activity.activityType === 'field_update' && activity.metadata?.fields?.length > 0) {
                  // Review Matrix fields
                  const reviewMatrixFields = [
                    'problemStatement', 'rcaPresentedById', 'reportedIssue', 'problemDate', 'actualServiceImpact',
                    'briefAnalysis', 'rootCause', 'detailedAnalysis', 'correctiveActions', 'chronologyOfEvents'
                  ];
                  const changedFields = activity.metadata.fields.map((f: any) => f.field);
                  const hasOnlyReviewMatrixFields = changedFields.every((f: string) => reviewMatrixFields.includes(f));
                  const hasAnyReviewMatrixFields = changedFields.some((f: string) => reviewMatrixFields.includes(f));

                  if (hasOnlyReviewMatrixFields) {
                    return 'Review Matrix Updated';
                  } else if (hasAnyReviewMatrixFields) {
                    return 'Fields Updated';
                  }
                  return 'Fields Updated';
                } else if (activity.activityType === 'field_update' && activity.metadata?.field) {
                  // Single field update
                  const fieldName = activity.metadata.field.replace(/Id$/, '').replace(/([A-Z])/g, ' $1').trim();
                  return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} Updated`;
                } else if (activity.activityType === 'assignment' || activity.activityType === 'assignment_change') {
                  return 'Assignment Changed';
                } else if (activity.activityType === 'approvals_reset') {
                  return 'Approvals Reset';
                } else if (activity.activityType === 'customer_approval_requested' || activity.activityType === 'CUSTOMER_APPROVAL_REQUESTED') {
                  return 'Customer Approval Requested';
                } else if (activity.activityType === 'customer_approval_resent' || activity.activityType === 'CUSTOMER_APPROVAL_RESENT') {
                  return 'Customer Approval Resent';
                } else if (activity.activityType === 'customer_approval_response') {
                  return 'Customer Approval Response';
                } else if (activity.activityType === 'CUSTOMER_APPROVAL_COMPLETED') {
                  return 'Customer Approved';
                } else if (activity.activityType === 'CUSTOMER_APPROVAL_REJECTED') {
                  return 'Customer Rejected';
                } else if (activity.activityType === 'created_from_problem') {
                  // Change created from a Problem
                  return 'Change Created from Problem';
                } else if (activity.activityType === 'duplicated') {
                  // Change duplicated from another Change
                  return 'Change Duplicated';
                } else if (activity.activityType === 'created') {
                  // Dynamic ticket type label based on metadata
                  const ticketType = activity.metadata?.ticketType;
                  const isDuplicated = activity.metadata?.duplicatedFromNumber || activity.metadata?.duplicatedFromChangeNumber;
                  const isFromIncident = activity.metadata?.createdFromIncidentNumber;
                  const isFromProblem = activity.metadata?.createdFromProblemNumber;
                  if (ticketType === 'SERVICE_REQUEST') return isFromProblem ? 'Service Request Created from Problem' : 'Service Request Created';
                  if (ticketType === 'INCIDENT') return isDuplicated ? 'Incident Duplicated' : 'Incident Created';
                  if (ticketType === 'PROBLEM') return isFromIncident ? 'Problem Created from Incident' : 'Problem Created';
                  if (ticketType === 'CHANGE') return isDuplicated ? 'Change Duplicated' : (isFromProblem ? 'Change Created from Problem' : 'Change Created');
                  if (ticketType === 'CASE') return 'Case Created';
                  return 'Ticket Created';
                } else if (activity.activityType === 'work_note_added') {
                  return activity.metadata?.isPublic ? 'Customer Note Added' : 'Work Note Added';
                } else if (activity.activityType === 'NOTE_ADDED_CUSTOMER') {
                  return 'Customer Note Added';
                } else if (activity.activityType === 'NOTE_ADDED_INTERNAL') {
                  return 'Internal Note Added';
                } else if (activity.activityType === 'attachment_added' || activity.activityType === 'ATTACHMENT_UPLOADED') {
                  return 'Attachment Uploaded';
                } else if (activity.activityType === 'attachment_removed' || activity.activityType === 'ATTACHMENT_DELETED') {
                  return 'Attachment Deleted';
                } else if (activity.activityType === 'priority_change') {
                  return 'Priority Changed';
                } else if (activity.activityType === 'ticket_type_conversion') {
                  return 'Ticket Type Converted';
                } else if (activity.activityType === 'relationship_added') {
                  const source = activity.metadata?.source;
                  if (source === 'duplicate') return 'Relationship Added via Duplication';
                  if (source === 'create_problem_from_incident') return 'Related Record Added from Incident';
                  return 'Related Record Added';
                } else if (activity.activityType === 'relationship_removed') {
                  return 'Relationship Removed';
                } else if (activity.activityType === 'problem_status_change') {
                  return 'Problem Status Changed';
                } else if (activity.activityType === 'problem_task_created') {
                  return 'Problem Task Created';
                } else if (activity.activityType === 'problem_task_state_changed') {
                  return 'Problem Task Status Changed';
                } else if (activity.activityType === 'problem_task_closed') {
                  return 'Problem Task Closed';
                } else if (activity.activityType === 'servicenow_child_activity') {
                  return activity.metadata?.childRecordType
                    ? `${activity.metadata.childRecordType} activity`
                    : 'ServiceNow child activity';
                } else if (activity.activityType === 'service_request_approved') {
                  return 'Service Request Approved';
                } else if (activity.activityType === 'service_request_rejected') {
                  return 'Service Request Rejected';
                } else if (activity.activityType === 'auto_created_from_alert') {
                  return 'Auto-Created from Alert';
                } else if (activity.activityType === 'resolved_auto_close') {
                  return 'Automatically Closed';
                } else if (activity.activityType === 'created_from_alert') {
                  return 'Created from Alert';
                } else if (activity.activityType === 'SLA_STARTED') {
                  return 'SLA Started';
                } else if (activity.activityType === 'SLA_PAUSED') {
                  return 'SLA Paused';
                } else if (activity.activityType === 'SLA_RESUMED') {
                  return 'SLA Resumed';
                } else if (activity.activityType === 'SLA_RESET') {
                  return 'SLA Reset';
                } else if (activity.activityType === 'SLA_CANCELLED') {
                  return 'SLA Cancelled';
                } else if (activity.activityType === 'SLA_COMPLETED') {
                  return 'SLA Completed';
                } else if (activity.activityType === 'SLA_BREACHED') {
                  return 'SLA Breached';
                } else if (activity.activityType === 'SLA_WARNING') {
                  return 'SLA Warning';
                } else if (activity.activityType === 'SLA_REOPENED') {
                  return 'SLA Reopened';
                } else if (activity.activityType === 'SLA_COMPLETED_TYPE_CHANGE') {
                  return 'SLA Completed (Type Change)';
                } else if (activity.activityType === 'SLA_DISCARDED') {
                  return 'SLA Discarded';
                } else if (activity.activityType === 'SLA_REEVALUATED') {
                  return 'SLA Re-evaluated';
                } else if (activity.activityType === 'peer_review_initiated') {
                  return 'Peer Review Initiated';
                } else if (activity.activityType === 'peer_review_approved') {
                  return 'Peer Review Approved';
                } else if (activity.activityType === 'peer_review_rejected') {
                  return 'Peer Review Rejected';
                } else if (activity.activityType === 'cab_approval_initiated') {
                  return 'CAB Approval Initiated';
                } else if (activity.activityType === 'cab_approved' || activity.activityType === 'cab_approval_approved') {
                  return 'CAB Approval Approved';
                } else if (activity.activityType === 'cab_rejected' || activity.activityType === 'cab_approval_rejected') {
                  return 'CAB Approval Rejected';
                } else if (activity.activityType === 'cab_approval_skipped') {
                  return 'CAB Approval Skipped';
                } else if (activity.activityType === 'customer_approval_initiated') {
                  return 'Customer Approval Initiated';
                } else if (activity.activityType === 'customer_approved') {
                  return 'Customer Approved';
                } else if (activity.activityType === 'customer_rejected') {
                  return 'Customer Rejected';
                } else if (activity.activityType === 'customer_approval_skipped') {
                  return 'Customer Approval Skipped';
                } else if (activity.activityType === 'response_breach_reason_added') {
                  return 'Response Breach Reason Added';
                } else if (activity.activityType === 'ongoing_breach_reason_added') {
                  return 'Ongoing Breach Reason Added';
                } else if (activity.activityType === 'resolution_breach_reason_added') {
                  return 'Resolution Breach Reason Added';
                }
                return activity.activityType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
              };

              // Render details with formatting
              const renderActivityDetails = (): React.JSX.Element => {
                if (activity.activityType === 'resolved_auto_close') {
                  const formatState = (state: string | null | undefined): string | null => {
                    if (!state) return null;
                    return state.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  const previousState = formatState(
                    activity.previousState ||
                      activity.metadata?.previousState ||
                      activity.metadata?.from ||
                      activity.metadata?.oldState
                  );
                  const newState = formatState(
                    activity.newState ||
                      activity.metadata?.newState ||
                      activity.metadata?.to
                  );

                  return (
                    <div>
                      {(previousState || newState) && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">State:</span>
                          <span className="text-gray-500 ml-1">{previousState || 'None'}</span>
                          <span className="text-gray-400 mx-1">→</span>
                          <span className="text-gray-900 font-medium">{newState || 'None'}</span>
                        </div>
                      )}
                      {activity.metadata?.message && (
                        <div className="mt-2 text-sm text-gray-700">
                          {activity.metadata.message}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for bulk update
                if (activity.activityType === 'bulk_update') {
                  const changes: React.JSX.Element[] = [];

                  // State change
                  if (activity.metadata?.previousState && activity.metadata?.newState) {
                    const oldState = activity.metadata.previousState.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    const newState = activity.metadata.newState.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    changes.push(
                      <div key="state" className="text-sm">
                        <span className="font-medium text-gray-700">State:</span>
                        <span className="text-gray-500 ml-1">{oldState}</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="text-gray-900 font-medium">{newState}</span>
                      </div>
                    );
                  }

                  // Priority change
                  if (activity.metadata?.priority) {
                    const priority = activity.metadata.priority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    changes.push(
                      <div key="priority" className="text-sm">
                        <span className="font-medium text-gray-700">Priority:</span>
                        <span className="text-gray-900 font-medium ml-1">{priority}</span>
                      </div>
                    );
                  }

                  // Assignment group change - show names if available
                  if (activity.metadata?.assignmentGroupUpdated || activity.metadata?.assignmentGroupId !== undefined) {
                    const previousGroupName = activity.metadata.previousAssignmentGroupName || 'None';
                    const newGroupName = activity.metadata.assignmentGroupName || 'None';
                    changes.push(
                      <div key="assignmentGroup" className="text-sm">
                        <span className="font-medium text-gray-700">Assignment Group:</span>
                        <span className="text-gray-500 ml-1">{previousGroupName}</span>
                        <span className="text-gray-400 mx-1">&rarr;</span>
                        <span className="text-gray-900 font-medium">{newGroupName}</span>
                      </div>
                    );
                  }

                  // Assigned to change - show names with self-assignment indicator
                  if (activity.metadata?.assignedToUpdated || activity.metadata?.assignedToId !== undefined) {
                    const previousName = activity.metadata.previousAssignedToName || 'None';
                    const newName = activity.metadata.newAssignedToName || activity.metadata.assignedToName || 'None';
                    const isSelfAssigned = activity.metadata.selfAssigned ||
                      (activity.User?.id && (
                        activity.metadata.newAssignedToId === activity.User.id ||
                        activity.metadata.assignedToId === activity.User.id
                      ));

                    changes.push(
                      <div key="assignedTo" className="text-sm">
                        <span className="font-medium text-gray-700">Assigned To:</span>
                        <span className="text-gray-500 ml-1">{previousName}</span>
                        <span className="text-gray-400 mx-1">&rarr;</span>
                        <span className="text-gray-900 font-medium">{newName}</span>
                        {isSelfAssigned && <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>}
                      </div>
                    );
                  }

                  // Notes and reasons
                  const notes: React.JSX.Element[] = [];
                  if (activity.metadata?.publicNote) {
                    notes.push(
                      <div key="publicNote" className="text-sm text-blue-700 mt-2">
                        <span className="font-medium">Customer Note:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.publicNote} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.internalNote) {
                    notes.push(
                      <div key="internalNote" className="text-sm text-gray-700 mt-2">
                        <span className="font-medium">Internal Note:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.internalNote} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.infoRequired) {
                    notes.push(
                      <div key="infoRequired" className="text-sm text-blue-700 mt-2">
                        <span className="font-medium">Info Required:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.infoRequired} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.onHoldReason) {
                    notes.push(
                      <div key="onHoldReason" className="text-sm text-amber-700 mt-2">
                        <span className="font-medium">On-Hold Reason:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.onHoldReason} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.resolutionNotes) {
                    notes.push(
                      <div key="resolutionNotes" className="text-sm text-green-700 mt-2">
                        <span className="font-medium">Resolution Notes:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.resolutionNotes} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.resolutionCode) {
                    const resolutionCode = activity.metadata.resolutionCode.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    notes.push(
                      <div key="resolutionCode" className="text-sm text-green-700 mt-2">
                        <span className="font-medium">Resolution Code:</span>
                        <span className="ml-1">{resolutionCode}</span>
                      </div>
                    );
                  }
                  if (activity.metadata?.implementationResults) {
                    notes.push(
                      <div key="implementationResults" className="text-sm text-blue-700 mt-2">
                        <span className="font-medium">Implementation Summary:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.implementationResults} />
                        </div>
                      </div>
                    );
                  }
                  if (activity.metadata?.reviewResults) {
                    notes.push(
                      <div key="reviewResults" className="text-sm text-purple-700 mt-2">
                        <span className="font-medium">Review Notes:</span>
                        <div className="mt-0.5">
                          <RichTextDisplay content={activity.metadata.reviewResults} />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div>
                      {changes.length > 0 && (
                        <div className="space-y-1">
                          {changes}
                        </div>
                      )}
                      {notes.length > 0 && (
                        <div>
                          {notes}
                        </div>
                      )}
                      {changes.length === 0 && notes.length === 0 && <div>-</div>}
                    </div>
                  );
                }

                // Check for consolidated field updates
                if (activity.activityType === 'field_update' && activity.metadata?.fields?.length > 0) {
                  // Review Matrix fields that should be grouped together
                  const reviewMatrixFields = [
                    'problemStatement', 'rcaPresentedById', 'reportedIssue', 'problemDate', 'actualServiceImpact',
                    'briefAnalysis', 'rootCause', 'detailedAnalysis', 'correctiveActions', 'chronologyOfEvents'
                  ];

                  const formatFieldName = (field: string) => {
                    const fieldLabels: Record<string, string> = {
                      priority: 'Priority',
                      assignedTo: 'Assigned To',
                      assignmentGroup: 'Assignment Group',
                      customer: 'Customer',
                      engagement: 'Engagement',
                      contact: 'Contact',
                      shortDescription: 'Short Description',
                      description: 'Description',
                      problemStatement: 'Problem Statement',
                      reportedIssue: 'Reported Issue',
                      actualServiceImpact: 'Actual Service Impact',
                      briefAnalysis: 'Brief Analysis',
                      rootCause: 'Root Cause',
                      detailedAnalysis: 'Detailed Analysis',
                      correctiveActions: 'Corrective Actions',
                      chronologyOfEvents: 'Chronology of Events'
                    };
                    return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  // Check if any Review Matrix fields were changed
                  const changedReviewMatrixFields = activity.metadata.fields.filter(
                    (change: any) => reviewMatrixFields.includes(change.field)
                  );
                  const nonReviewMatrixFields = activity.metadata.fields.filter(
                    (change: any) => !reviewMatrixFields.includes(change.field)
                  );

                  // If only Review Matrix fields changed, show "Review Matrix changed"
                  if (changedReviewMatrixFields.length > 0 && nonReviewMatrixFields.length === 0) {
                    return (
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Review Matrix</span>
                        <span className="text-gray-500 ml-1">changed</span>
                      </div>
                    );
                  }

                  // If mixed fields, show Review Matrix and other fields separately
                  const hasReviewMatrixChanges = changedReviewMatrixFields.length > 0 && nonReviewMatrixFields.length > 0;
                  if (hasReviewMatrixChanges) {
                    // Continue with normal processing for non-review-matrix fields
                    activity.metadata.fields = nonReviewMatrixFields;
                  }

                  const formatFieldValue = (field: string, value: any, label?: string) => {
                    // Use label if available (for IDs that have been enriched with names)
                    if (label) return label;
                    if (value === null || value === undefined) return 'None';
                    if (field === 'priority') {
                      return value.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    }
                    return value;
                  };

                  // Helper to strip HTML tags for display
                  const stripHtmlForDisplay = (html: string | null | undefined): string => {
                    const text = stripHtml(html);
                    return text || 'None';
                  };

                  // Fields that contain rich text/HTML content
                  const richTextFields = ['description', 'shortDescription', 'problemStatement', 'reportedIssue',
                    'actualServiceImpact', 'briefAnalysis', 'rootCause', 'detailedAnalysis',
                    'correctiveActions', 'chronologyOfEvents', 'resolution', 'resolutionNotes'];

                  return (
                    <div className="space-y-2">
                      {activity.metadata.fields.map((change: any, index: number) => {
                        // For rich text fields, use RichTextDisplay or strip HTML
                        if (richTextFields.includes(change.field)) {
                          return (
                            <div key={index} className="text-sm">
                              <span className="font-medium text-gray-700">{formatFieldName(change.field)}:</span>
                              <span className="text-gray-500 ml-1">
                                {stripHtmlForDisplay(change.previousValue)}
                              </span>
                              <span className="text-gray-400 mx-1">→</span>
                              <span className="text-gray-900 font-medium">
                                {stripHtmlForDisplay(change.newValue)}
                              </span>
                            </div>
                          );
                        }

                        // Check for self-assignment: when the user who performed the action is the same as the new assignee
                        const isSelfAssignment = change.field === 'assignedTo' &&
                          activity.User?.id &&
                          change.newValue === activity.User.id;

                        // For assignedTo field with self-assignment, show special text
                        if (change.field === 'assignedTo' && isSelfAssignment) {
                          const userName = `${activity.User.firstName || ''} ${activity.User.lastName || ''}`.trim() || activity.User.email;
                          return (
                            <div key={index} className="text-sm">
                              <span className="font-medium text-gray-700">{formatFieldName(change.field)}:</span>
                              <span className="text-gray-500 ml-1">
                                {formatFieldValue(change.field, change.previousValue, change.previousValueLabel)}
                              </span>
                              <span className="text-gray-400 mx-1">→</span>
                              <span className="text-gray-900 font-medium">
                                {userName}
                              </span>
                              <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>
                            </div>
                          );
                        }

                        // For other fields, use regular display
                        return (
                          <div key={index} className="text-sm">
                            <span className="font-medium text-gray-700">{formatFieldName(change.field)}:</span>
                            <span className="text-gray-500 ml-1">
                              {formatFieldValue(change.field, change.previousValue, change.previousValueLabel)}
                            </span>
                            <span className="text-gray-400 mx-1">→</span>
                            <span className="text-gray-900 font-medium">
                              {formatFieldValue(change.field, change.newValue, change.newValueLabel)}
                            </span>
                          </div>
                        );
                      })}
                      {/* Show Review Matrix summary if there were also Review Matrix changes */}
                      {hasReviewMatrixChanges && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Review Matrix</span>
                          <span className="text-gray-500 ml-1">changed</span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for approvals reset
                if (activity.activityType === 'approvals_reset') {
                  return (
                    <div>
                      <div className="text-gray-700">{activity.metadata?.reason || 'All approvals have been reset'}</div>
                      {activity.metadata?.newState && (
                        <div className="text-sm text-gray-500 mt-1">
                          New state: {activity.metadata.newState.charAt(0).toUpperCase() + activity.metadata.newState.slice(1)}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer approval requested
                if (activity.activityType === 'customer_approval_requested') {
                  return (
                    <div>
                      <div className="text-gray-700">Sent to customer for approval</div>
                      {activity.metadata?.customerContactName && (
                        <div className="text-sm text-gray-500 mt-1">
                          Contact: {activity.metadata.customerContactName}
                        </div>
                      )}
                      {activity.metadata?.customerContactEmail && (
                        <div className="text-sm text-gray-500">
                          Email: {activity.metadata.customerContactEmail}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer approval response
                if (activity.activityType === 'customer_approval_response') {
                  const decision = activity.metadata?.decision || '';
                  const decisionColor = decision === 'approved' ? 'text-green-700' : 'text-red-700';
                  return (
                    <div>
                      <div className={`font-medium ${decisionColor}`}>
                        {decision.charAt(0).toUpperCase() + decision.slice(1)}
                      </div>
                      {activity.metadata?.comments && (
                        <div className="text-gray-600 mt-1">
                          <RichTextDisplay content={activity.metadata.comments} />
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for CUSTOMER_APPROVAL_REQUESTED (uppercase version)
                if (activity.activityType === 'CUSTOMER_APPROVAL_REQUESTED') {
                  return (
                    <div>
                      <div className="text-gray-700">
                        Sent to {activity.metadata?.customerContactCount || 1} recipient(s)
                      </div>
                      {activity.metadata?.customerContactNames && (
                        <div className="text-sm text-gray-500 mt-1">
                          To: {activity.metadata.customerContactNames}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for CUSTOMER_APPROVAL_RESENT (uppercase version)
                if (activity.activityType === 'CUSTOMER_APPROVAL_RESENT') {
                  return (
                    <div>
                      <div className="text-gray-700">
                        Resent to {activity.metadata?.customerContactCount || 1} recipient(s)
                      </div>
                      {activity.metadata?.customerContactNames && (
                        <div className="text-sm text-gray-500 mt-1">
                          To: {activity.metadata.customerContactNames}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for peer_review_initiated
                if (activity.activityType === 'peer_review_initiated') {
                  const membersRequested = activity.metadata?.membersRequested || [];
                  const groupName = activity.metadata?.peerReviewGroupName || 'Unknown Group';
                  const requestedAt = activity.metadata?.requestedAt ? formatDateTime(activity.metadata.requestedAt) : '';
                  return (
                    <div>
                      <div className="text-gray-700">
                        Peer review requested from <span className="font-medium">{groupName}</span>
                      </div>
                      {requestedAt && (
                        <div className="text-sm text-gray-500 mt-1">
                          Requested at: {requestedAt}
                        </div>
                      )}
                      {membersRequested.length > 0 && (
                        <div className="mt-2">
                          <div className="text-sm font-medium text-gray-600 mb-1">Reviewers notified:</div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {membersRequested.map((member: { id: string; name: string; email: string }, idx: number) => (
                              <div key={member.id || idx} className="flex items-center">
                                <span className="text-gray-700">{member.name || 'Unknown'}</span>
                                <span className="text-gray-400 mx-1">-</span>
                                <span className="text-gray-500">{member.email}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for peer_review_approved
                if (activity.activityType === 'peer_review_approved') {
                  const approvedBy = activity.metadata?.approvedBy || 'Unknown';
                  const comments = activity.metadata?.comments;
                  const nextStage = activity.metadata?.nextStage;
                  return (
                    <div>
                      <div className="text-green-700 font-medium">
                        Approved by {approvedBy}
                      </div>
                      {comments && (
                        <div className="text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <div className="text-sm font-medium text-gray-500 mb-1">Comments:</div>
                          <RichTextDisplay content={comments} />
                        </div>
                      )}
                      {nextStage && (
                        <div className="text-sm text-gray-500 mt-1">
                          Next stage: {nextStage.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for peer_review_rejected
                if (activity.activityType === 'peer_review_rejected') {
                  const rejectedBy = activity.metadata?.rejectedBy || 'Unknown';
                  const comments = activity.metadata?.comments;
                  return (
                    <div>
                      <div className="text-red-700 font-medium">
                        Rejected by {rejectedBy}
                      </div>
                      {comments && (
                        <div className="text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <div className="text-sm font-medium text-gray-500 mb-1">Comments:</div>
                          <RichTextDisplay content={comments} />
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for cab_approval_initiated
                if (activity.activityType === 'cab_approval_initiated') {
                  const membersRequested = activity.metadata?.membersRequested || [];
                  const groupName = activity.metadata?.cabGroupName || 'Unknown Group';
                  const requestedAt = activity.metadata?.requestedAt ? formatDateTime(activity.metadata.requestedAt) : '';
                  return (
                    <div>
                      <div className="text-gray-700">
                        CAB approval requested from <span className="font-medium">{groupName}</span>
                      </div>
                      {requestedAt && (
                        <div className="text-sm text-gray-500 mt-1">
                          Requested at: {requestedAt}
                        </div>
                      )}
                      {membersRequested.length > 0 && (
                        <div className="mt-2">
                          <div className="text-sm font-medium text-gray-600 mb-1">CAB members notified:</div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {membersRequested.map((member: { id: string; name: string; email: string }, idx: number) => (
                              <div key={member.id || idx} className="flex items-center">
                                <span className="text-gray-700">{member.name || 'Unknown'}</span>
                                <span className="text-gray-400 mx-1">-</span>
                                <span className="text-gray-500">{member.email}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for cab_approved or cab_approval_approved
                if (activity.activityType === 'cab_approved' || activity.activityType === 'cab_approval_approved') {
                  // Fallback chain: metadata.approverName -> metadata.approvedBy -> activity.User (performedBy) -> 'Unknown'
                  const performedByName = activity.User ? `${activity.User.firstName || ''} ${activity.User.lastName || ''}`.trim() : null;
                  const approverName = activity.metadata?.approverName || activity.metadata?.approvedBy || performedByName || 'Unknown';
                  const approverEmail = activity.metadata?.approverEmail || activity.User?.email;
                  const comments = activity.metadata?.comments;
                  const nextStage = activity.metadata?.nextStage;
                  return (
                    <div>
                      <div className="text-green-700 font-medium">
                        Approved by {approverName}
                      </div>
                      {approverEmail && (
                        <div className="text-sm text-gray-500">
                          {approverEmail}
                        </div>
                      )}
                      {comments && (
                        <div className="text-gray-600 mt-2 p-2 bg-green-50 rounded border border-green-100">
                          <div className="text-sm font-medium text-green-700 mb-1">Approval Comments:</div>
                          <RichTextDisplay content={comments} />
                        </div>
                      )}
                      {nextStage && (
                        <div className="text-sm text-gray-500 mt-1">
                          Next stage: {nextStage.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for cab_rejected or cab_approval_rejected
                if (activity.activityType === 'cab_rejected' || activity.activityType === 'cab_approval_rejected') {
                  // Fallback chain: metadata.approverName -> metadata.rejectedBy -> activity.User (performedBy) -> 'Unknown'
                  const performedByName = activity.User ? `${activity.User.firstName || ''} ${activity.User.lastName || ''}`.trim() : null;
                  const approverName = activity.metadata?.approverName || activity.metadata?.rejectedBy || performedByName || 'Unknown';
                  const approverEmail = activity.metadata?.approverEmail || activity.User?.email;
                  const comments = activity.metadata?.comments;
                  const rejectionReason = activity.metadata?.rejectionReason || comments;
                  return (
                    <div>
                      <div className="text-red-700 font-medium">
                        Rejected by {approverName}
                      </div>
                      {approverEmail && (
                        <div className="text-sm text-gray-500">
                          {approverEmail}
                        </div>
                      )}
                      {rejectionReason && (
                        <div className="mt-2">
                          <div className="text-sm font-medium text-red-600">Rejection Reason:</div>
                          <div className="text-gray-700 mt-1 p-2 bg-red-50 rounded border border-red-100">
                            <RichTextDisplay content={rejectionReason} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer_approval_initiated (Change workflow)
                if (activity.activityType === 'customer_approval_initiated') {
                  const contactsRequested = activity.metadata?.contactsRequested || [];
                  const requestedAt = activity.metadata?.requestedAt ? formatDateTime(activity.metadata.requestedAt) : '';
                  return (
                    <div>
                      <div className="text-gray-700">
                        Customer approval requested
                      </div>
                      {requestedAt && (
                        <div className="text-sm text-gray-500 mt-1">
                          Requested at: {requestedAt}
                        </div>
                      )}
                      {contactsRequested.length > 0 && (
                        <div className="mt-2">
                          <div className="text-sm font-medium text-gray-600 mb-1">Contacts notified:</div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {contactsRequested.map((contact: { id: string; name: string; email: string }, idx: number) => (
                              <div key={contact.id || idx} className="flex items-center">
                                <span className="text-gray-700">{contact.name || 'Unknown'}</span>
                                <span className="text-gray-400 mx-1">-</span>
                                <span className="text-gray-500">{contact.email}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer_approved (Change workflow)
                if (activity.activityType === 'customer_approved') {
                  const approvedBy = activity.metadata?.approvedBy || 'Unknown';
                  const comments = activity.metadata?.comments;
                  const nextStage = activity.metadata?.nextStage;
                  return (
                    <div>
                      <div className="text-green-700 font-medium">
                        Approved by {approvedBy}
                      </div>
                      {comments && (
                        <div className="text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <div className="text-sm font-medium text-gray-500 mb-1">Comments:</div>
                          <RichTextDisplay content={comments} />
                        </div>
                      )}
                      {nextStage && (
                        <div className="text-sm text-gray-500 mt-1">
                          Next stage: {nextStage.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer_rejected (Change workflow)
                if (activity.activityType === 'customer_rejected') {
                  const rejectedBy = activity.metadata?.rejectedBy || 'Unknown';
                  const comments = activity.metadata?.comments;
                  return (
                    <div>
                      <div className="text-red-700 font-medium">
                        Rejected by {rejectedBy}
                      </div>
                      {comments && (
                        <div className="text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <div className="text-sm font-medium text-gray-500 mb-1">Comments:</div>
                          <RichTextDisplay content={comments} />
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for customer_approval_skipped
                if (activity.activityType === 'customer_approval_skipped') {
                  const reason = activity.metadata?.reason || 'No reason provided';
                  return (
                    <div>
                      <div className="text-yellow-700 font-medium">
                        Customer approval skipped
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Reason: {reason}
                      </div>
                    </div>
                  );
                }

                // Check for cab_approval_skipped
                if (activity.activityType === 'cab_approval_skipped') {
                  const reason = activity.metadata?.reason || 'No reason provided';
                  const suggestion = activity.metadata?.suggestion;
                  const cabGroupName = activity.metadata?.cabGroupName;
                  return (
                    <div>
                      <div className="text-red-700 font-medium">
                        CAB approval could not be initiated
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <strong>Reason:</strong> {reason}
                      </div>
                      {cabGroupName && (
                        <div className="text-sm text-gray-600 mt-1">
                          <strong>CAB Group:</strong> {cabGroupName}
                        </div>
                      )}
                      {suggestion && (
                        <div className="text-sm text-blue-600 mt-2 p-2 bg-blue-50 rounded">
                          <strong>Action Required:</strong> {suggestion}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for relationship_added
                if (activity.activityType === 'relationship_added') {
                  const targetCaseNumber = activity.metadata?.targetCaseNumber || 'Unknown';
                  const source = activity.metadata?.source;

                  if (source === 'duplicate') {
                    return (
                      <div>
                        <div className="text-gray-700">
                          Relationship added via duplicating Incident
                        </div>
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Related to:</span>
                          <span className="ml-1 text-blue-600">{targetCaseNumber}</span>
                        </div>
                      </div>
                    );
                  }

                  if (source === 'create_problem_from_incident') {
                    return (
                      <div>
                        <div className="text-gray-700">
                          Related record added by creating problem from Incident
                        </div>
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Related to:</span>
                          <span className="ml-1 text-blue-600">{targetCaseNumber}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div>
                      <div className="text-gray-700">
                        Related Record Added
                      </div>
                      <div className="text-gray-700 mt-1">
                        <span className="font-medium">Related to:</span>
                        <span className="ml-1 text-blue-600">{targetCaseNumber}</span>
                      </div>
                    </div>
                  );
                }

                // Check for relationship_removed
                if (activity.activityType === 'relationship_removed') {
                  const relationshipTypeLabels: Record<string, string> = {
                    'related_to': 'Related to',
                    'caused_by': 'Caused by',
                    'duplicate_of': 'Duplicate of'
                  };
                  const relationshipType = activity.metadata?.relationshipType || 'related_to';
                  const relationshipLabel = relationshipTypeLabels[relationshipType] || relationshipType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  const targetCaseNumber = activity.metadata?.targetCaseNumber || 'Unknown';

                  return (
                    <div>
                      <div className="text-gray-700">
                        <span className="font-medium">{relationshipLabel}:</span>
                        <span className="ml-1 text-red-600 line-through">{targetCaseNumber}</span>
                        <span className="ml-1 text-gray-500">(removed)</span>
                      </div>
                    </div>
                  );
                }

                // Check for problem_task_created
                if (activity.activityType === 'problem_task_created') {
                  const taskNumber = activity.metadata?.taskNumber || 'Unknown';
                  const taskType = activity.metadata?.taskType;
                  const assignedTo = activity.metadata?.assignedTo;
                  const dueDate = activity.metadata?.dueDate;

                  const taskTypeLabels: Record<string, string> = {
                    'root_cause_analysis': 'Root Cause Analysis',
                    'general': 'General'
                  };
                  const assignedToLabels: Record<string, string> = {
                    'customer': 'Customer',
                    'infogain': 'Infogain',
                    'vendor': 'Vendor'
                  };

                  return (
                    <div>
                      <div className="text-gray-700">
                        <span className="font-medium">Task Number:</span>
                        <button
                          onClick={() => onTabChange?.('tasks')}
                          className="ml-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0 font-inherit"
                        >
                          {taskNumber}
                        </button>
                      </div>
                      {taskType && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Type:</span>
                          <span className="ml-1">{taskTypeLabels[taskType] || taskType}</span>
                        </div>
                      )}
                      {assignedTo && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Owner:</span>
                          <span className="ml-1">{assignedToLabels[assignedTo] || assignedTo}</span>
                        </div>
                      )}
                      {dueDate && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Due Date:</span>
                          <span className="ml-1">{formatDate(dueDate)}</span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for problem_task_state_changed
                if (activity.activityType === 'problem_task_state_changed') {
                  const taskNumber = activity.metadata?.taskNumber || 'Unknown';
                  const previousState = activity.metadata?.previousState || 'Unknown';
                  const newState = activity.metadata?.newState || 'Unknown';
                  const assignmentGroup = activity.metadata?.assignmentGroup;
                  const assignedTo = activity.metadata?.assignedTo;

                  return (
                    <div>
                      <div className="text-gray-700">
                        <span className="font-medium">Task:</span>
                        <button
                          onClick={() => onTabChange?.('tasks')}
                          className="ml-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0 font-inherit"
                        >
                          {taskNumber}
                        </button>
                      </div>
                      <div className="text-gray-700 mt-1">
                        <span className="font-medium">Status:</span>
                        <span className="ml-1">{previousState}</span>
                        <span className="mx-1">→</span>
                        <span>{newState}</span>
                      </div>
                      {assignmentGroup && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Assignment Group:</span>
                          <span className="ml-1">
                            {assignmentGroup.oldName || 'None'}
                            <span className="mx-1">→</span>
                            {assignmentGroup.newName || 'None'}
                          </span>
                        </div>
                      )}
                      {assignedTo && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Assigned To:</span>
                          <span className="ml-1">
                            {assignedTo.oldName || 'None'}
                            <span className="mx-1">→</span>
                            {assignedTo.newName || 'None'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for problem_task_closed
                if (activity.activityType === 'problem_task_closed') {
                  const taskNumber = activity.metadata?.taskNumber || 'Unknown';
                  const closedCode = activity.metadata?.closedCode;
                  const causeCode = activity.metadata?.causeCode;

                  return (
                    <div>
                      <div className="text-gray-700">
                        <span className="font-medium">Task:</span>
                        <button
                          onClick={() => onTabChange?.('tasks')}
                          className="ml-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0 font-inherit"
                        >
                          {taskNumber}
                        </button>
                      </div>
                      {closedCode && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Closed Code:</span>
                          <span className="ml-1">{closedCode}</span>
                        </div>
                      )}
                      {causeCode && (
                        <div className="text-gray-700 mt-1">
                          <span className="font-medium">Cause Code:</span>
                          <span className="ml-1">{causeCode}</span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for CUSTOMER_APPROVAL_COMPLETED (uppercase version)
                if (activity.activityType === 'CUSTOMER_APPROVAL_COMPLETED') {
                  const approverName = activity.metadata?.approvedBy || activity.User?.firstName + ' ' + activity.User?.lastName;
                  const notes = activity.metadata?.comments || activity.metadata?.customerNotes;
                  return (
                    <div>
                      <div className="text-green-700 font-medium">
                        Fix Approved by {approverName}
                      </div>
                      {notes && (
                        <div className="text-sm text-gray-600 mt-1">
                          Notes: {notes}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for CUSTOMER_APPROVAL_REJECTED (uppercase version)
                if (activity.activityType === 'CUSTOMER_APPROVAL_REJECTED') {
                  const rejectorName = activity.metadata?.rejectedBy || activity.User?.firstName + ' ' + activity.User?.lastName;
                  const reason = activity.metadata?.comments || activity.metadata?.customerNotes;
                  return (
                    <div>
                      <div className="text-red-700 font-medium">
                        Fix Rejected by {rejectorName}
                      </div>
                      {reason && (
                        <div className="text-sm text-gray-600 mt-1">
                          Reason: {reason}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for assignment update with names (new format from "Assign to me" button)
                if (activity.activityType === 'field_update' && activity.metadata?.assignedToUpdated) {
                  const previousName = activity.metadata.previousAssignedToName || 'None';
                  const newName = activity.metadata.newAssignedToName || 'None';
                  const isSelfAssigned = activity.metadata.selfAssigned ||
                    (activity.User?.id && (
                      activity.metadata.newAssignedToId === activity.User.id ||
                      activity.metadata.assignedToId === activity.User.id
                    ));

                  return (
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Assigned To:</span>
                        <span className="text-gray-500 ml-1">{previousName}</span>
                        <span className="text-gray-400 mx-1">&rarr;</span>
                        <span className="text-gray-900 font-medium">{newName}</span>
                        {isSelfAssigned && <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>}
                      </div>
                    </div>
                  );
                }

                // Check for single field update (legacy format with field/oldValue/newValue)
                if (activity.activityType === 'field_update' && activity.metadata?.field) {
                  const fieldName = activity.metadata.field.replace(/Id$/, '').replace(/([A-Z])/g, ' $1').trim();
                  const formattedFieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
                  return (
                    <div>
                      <div className="text-sm">
                        <span className="text-gray-700">{formattedFieldName}:</span>
                        {activity.metadata.oldValue && (
                          <span className="text-gray-500 ml-1">{activity.metadata.oldValue}</span>
                        )}
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="text-gray-900 font-medium">{activity.metadata.newValue || 'Not set'}</span>
                      </div>
                      {activity.metadata.context && (
                        <div className="text-xs text-gray-500 mt-1">
                          {activity.metadata.context}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for state change with notes
                if (activity.activityType === 'state_change' || activity.activityType === 'problem_status_change') {
                  let previousState: string | null = null;
                  let newState: string | null = null;
                  let noteContent = null;
                  let noteLabel = '';
                  let noteColor = '';
                  let additionalInfo: string[] = [];
                  let fieldChanges: React.JSX.Element[] = [];

                  // Extract previous and new state from various possible locations
                  // Priority: root level fields > metadata.from/to > metadata.previousState/newState
                  const formatState = (state: string | null | undefined): string | null => {
                    if (!state) return null;
                    return state.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  // Get previous state (check root level first, then metadata)
                  previousState = formatState(
                    activity.previousState ||
                    activity.metadata?.previousState ||
                    activity.metadata?.from ||
                    activity.metadata?.oldState
                  );

                  // Get new state (check root level first, then metadata)
                  newState = formatState(
                    activity.newState ||
                    activity.metadata?.newState ||
                    activity.metadata?.to
                  );

                  // Check for assignment changes in metadata with human-readable names
                  if (activity.metadata?.assignmentGroupUpdated) {
                    const previousName = activity.metadata.previousAssignmentGroupName || 'None';
                    // Support both field naming conventions (assignmentGroupName or newAssignmentGroupName)
                    const newName = activity.metadata.newAssignmentGroupName || activity.metadata.assignmentGroupName || 'None';
                    fieldChanges.push(
                      <div key="assignmentGroup" className="text-sm">
                        <span className="font-medium text-gray-700">Assignment Group:</span>
                        <span className="text-gray-500 ml-1">{previousName}</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="text-gray-900 font-medium">{newName}</span>
                      </div>
                    );
                  }
                  if (activity.metadata?.assignedToUpdated) {
                    const previousName = activity.metadata.previousAssignedToName || 'None';
                    // Support both field naming conventions (assignedToName or newAssignedToName)
                    const newName = activity.metadata.newAssignedToName || activity.metadata.assignedToName || 'None';
                    // Check for self-assignment
                    const isSelfAssignment = activity.User?.id &&
                      (activity.metadata?.newAssignedToId === activity.User.id ||
                       activity.metadata?.assignedToId === activity.User.id);
                    fieldChanges.push(
                      <div key="assignedTo" className="text-sm">
                        <span className="font-medium text-gray-700">Assigned To:</span>
                        <span className="text-gray-500 ml-1">{previousName}</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="text-gray-900 font-medium">{newName}</span>
                        {isSelfAssignment && <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>}
                      </div>
                    );
                  }

                  // Problem-specific summary field updates with content
                  // Show Assessment 1 Summary if available (either updated or captured for state transition)
                  if (activity.metadata?.assessment1Summary) {
                    const label = activity.metadata?.assessment1SummaryUpdated ? 'Assessment 1 Summary (Updated):' : 'Assessment 1 Summary:';
                    fieldChanges.push(
                      <div key="assessment1Summary" className="text-sm mt-2">
                        <span className="font-medium text-blue-700">{label}</span>
                        <div className="mt-1 pl-3 border-l-2 border-blue-200">
                          <RichTextDisplay content={activity.metadata.assessment1Summary} />
                        </div>
                      </div>
                    );
                  }
                  // Show Assessment 2 Summary if available
                  if (activity.metadata?.assessment2Summary) {
                    const label = activity.metadata?.assessment2SummaryUpdated ? 'Assessment 2 Summary (Updated):' : 'Assessment 2 Summary:';
                    fieldChanges.push(
                      <div key="assessment2Summary" className="text-sm mt-2">
                        <span className="font-medium text-blue-700">{label}</span>
                        <div className="mt-1 pl-3 border-l-2 border-blue-200">
                          <RichTextDisplay content={activity.metadata.assessment2Summary} />
                        </div>
                      </div>
                    );
                  }
                  // Show Root Cause if available
                  if (activity.metadata?.rootCause) {
                    const label = activity.metadata?.rootCauseUpdated ? 'Root Cause (Updated):' : 'Root Cause:';
                    fieldChanges.push(
                      <div key="rootCause" className="text-sm mt-2">
                        <span className="font-medium text-purple-700">{label}</span>
                        <div className="mt-1 pl-3 border-l-2 border-purple-200">
                          <RichTextDisplay content={activity.metadata.rootCause} />
                        </div>
                      </div>
                    );
                  }
                  // Show Workaround if available
                  if (activity.metadata?.workaround) {
                    const label = activity.metadata?.workaroundUpdated ? 'Workaround (Updated):' : 'Workaround:';
                    fieldChanges.push(
                      <div key="workaround" className="text-sm mt-2">
                        <span className="font-medium text-amber-700">{label}</span>
                        <div className="mt-1 pl-3 border-l-2 border-amber-200">
                          <RichTextDisplay content={activity.metadata.workaround} />
                        </div>
                      </div>
                    );
                  }
                  // Show Solution Implemented status if available
                  if (activity.metadata?.solutionImplemented !== undefined) {
                    fieldChanges.push(
                      <div key="solutionImplemented" className="text-sm">
                        <span className="font-medium text-green-700">Solution Implemented:</span>
                        <span className="text-gray-600 ml-1">{activity.metadata.solutionImplemented ? 'Yes' : 'No'}</span>
                      </div>
                    );
                  }
                  // Show Implementation Results if available (for Changes)
                  if (activity.metadata?.implementationResults) {
                    fieldChanges.push(
                      <div key="implementationResults" className="text-sm mt-2">
                        <span className="font-medium text-blue-700">Implementation Summary:</span>
                        <div className="mt-1 pl-3 border-l-2 border-blue-200">
                          <RichTextDisplay content={activity.metadata.implementationResults} />
                        </div>
                      </div>
                    );
                  }
                  // Show Review Results if available (for Changes)
                  if (activity.metadata?.reviewResults) {
                    fieldChanges.push(
                      <div key="reviewResults" className="text-sm mt-2">
                        <span className="font-medium text-purple-700">Review Notes:</span>
                        <div className="mt-1 pl-3 border-l-2 border-purple-200">
                          <RichTextDisplay content={activity.metadata.reviewResults} />
                        </div>
                      </div>
                    );
                  }
                  // Show Closure Code if available (for Changes)
                  if (activity.metadata?.changeCloseCode) {
                    fieldChanges.push(
                      <div key="changeCloseCode" className="text-sm mt-2">
                        <span className="font-medium text-gray-700">Closure Code:</span>
                        <span className="ml-2 text-gray-600">{activity.metadata.changeCloseCode}</span>
                      </div>
                    );
                  }
                  // Show Closure Notes if available (for Changes)
                  if (activity.metadata?.closureNotes) {
                    fieldChanges.push(
                      <div key="closureNotes" className="text-sm mt-2">
                        <span className="font-medium text-gray-700">Closure Notes:</span>
                        <div className="mt-1 pl-3 border-l-2 border-gray-300">
                          <RichTextDisplay content={activity.metadata.closureNotes} />
                        </div>
                      </div>
                    );
                  }

                  // Determine which note to show and its color
                  if (activity.metadata?.infoRequired) {
                    noteLabel = 'Info Required:';
                    noteContent = activity.metadata.infoRequired;
                    noteColor = 'text-blue-700';
                  } else if (activity.metadata?.onHoldReason) {
                    noteLabel = 'On-Hold Reason:';
                    noteContent = activity.metadata.onHoldReason;
                    noteColor = 'text-amber-700';
                  } else if (activity.metadata?.solution) {
                    noteLabel = 'Proposed Solution:';
                    noteContent = activity.metadata.solution;
                    noteColor = 'text-purple-700';
                  } else if (activity.metadata?.resolution) {
                    noteLabel = 'Resolution:';
                    noteContent = activity.metadata.resolution;
                    noteColor = 'text-green-700';
                  } else if (activity.metadata?.resolutionNotes) {
                    noteLabel = 'Resolution Notes:';
                    noteContent = activity.metadata.resolutionNotes;
                    noteColor = 'text-green-700';
                  } else if (activity.metadata?.cancellationReason) {
                    noteLabel = 'Cancellation Reason:';
                    noteContent = activity.metadata.cancellationReason;
                    noteColor = 'text-red-700';
                  } else if (activity.metadata?.approvalNotes) {
                    noteLabel = 'Approval Notes:';
                    noteContent = activity.metadata.approvalNotes;
                    noteColor = 'text-green-700';
                  } else if (activity.metadata?.reopenReason) {
                    noteLabel = 'Reopen Reason:';
                    noteContent = activity.metadata.reopenReason;
                    noteColor = 'text-blue-700';
                  } else if (activity.metadata?.reason && (activity.metadata?.triggeredBy === 'customer_rejection' || activity.metadata?.triggeredBy === 'approval_rejection')) {
                    noteLabel = 'Rejection Reason:';
                    noteContent = activity.metadata.reason;
                    noteColor = 'text-red-700';
                  }

                  return (
                    <div>
                      {/* State transition display */}
                      {(previousState || newState) && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">State:</span>
                          <span className="text-gray-500 ml-1">{previousState || 'None'}</span>
                          <span className="text-gray-400 mx-1">→</span>
                          <span className="text-gray-900 font-medium">{newState || 'None'}</span>
                        </div>
                      )}
                      {!previousState && !newState && <div className="text-gray-500">-</div>}
                      {fieldChanges.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {fieldChanges}
                        </div>
                      )}
                      {activity.metadata?.approvalsReset && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium text-amber-700">Approvals Reset:</span>
                          <span className="text-gray-600 ml-1">
                            {activity.metadata.approvalsResetReason || 'All approvals have been reset'}
                          </span>
                        </div>
                      )}
                      {additionalInfo.length > 0 && (
                        <div className="text-sm text-gray-500 mt-1">
                          {additionalInfo.join(', ')}
                        </div>
                      )}
                      {noteContent && (
                        <div className={`mt-1 ${noteColor}`}>
                          <span className="font-medium">{noteLabel}</span>
                          <div className="mt-0.5">
                            <RichTextDisplay content={noteContent} />
                          </div>
                        </div>
                      )}
                      {/* Display closure/resolution information when state changes to resolved or closed */}
                      {(activity.metadata?.newState === 'closed' || activity.newState === 'closed' ||
                        activity.metadata?.newState === 'resolved' || activity.newState === 'resolved') && (
                        <div className="mt-2 text-sm space-y-1">
                          {activity.metadata?.resolutionCode && (
                            <div className="text-gray-700">
                              <span className="font-medium">Resolution Code:</span>
                              <span className="ml-1">{activity.metadata.resolutionCode.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                            </div>
                          )}
                          {activity.metadata?.resolutionNotes && (
                            <div className="text-gray-700">
                              <span className="font-medium">Resolution Notes:</span>
                              <div className="mt-0.5">
                                <RichTextDisplay content={activity.metadata.resolutionNotes} />
                              </div>
                            </div>
                          )}
                          {activity.metadata?.cause && (
                            <div className="text-gray-700">
                              <span className="font-medium">Cause:</span>
                              <div className="mt-0.5">
                                <RichTextDisplay content={activity.metadata.cause} />
                              </div>
                            </div>
                          )}
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => {
                                // Find the Closure tab button and click it
                                const tabs = document.querySelectorAll('button');
                                tabs.forEach(tab => {
                                  // Look for Closure tab - it may have badge indicators so check if text starts with 'Closure'
                                  const tabText = tab.textContent?.trim();
                                  if (tabText?.startsWith('Closure') && !tabText?.includes('Closure Notes')) {
                                    tab.click();
                                  }
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-xs cursor-pointer"
                            >
                              View full closure details in Closure tab →
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Display info request recipients for Awaiting Info state */}
                      {(activity.metadata?.caseContactName || activity.metadata?.caseContactEmail || (activity.metadata?.contactEmails && activity.metadata.contactEmails.length > 0)) && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium text-gray-700">Info Request Sent To:</span>
                          <span className="text-gray-900 ml-1">
                            {(() => {
                              const recipients: string[] = [];
                              // Add case contact if available (name preferred, fallback to email)
                              if (activity.metadata?.caseContactName || activity.metadata?.caseContactEmail) {
                                const contactDisplay = activity.metadata.caseContactName || activity.metadata.caseContactEmail;
                                recipients.push(`${contactDisplay} (Case Contact)`);
                              }
                              // Add additional email addresses from the modal
                              if (activity.metadata?.contactEmails && activity.metadata.contactEmails.length > 0) {
                                recipients.push(...activity.metadata.contactEmails);
                              }
                              return recipients.join(', ');
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for auto-created from alert
                if (activity.activityType === 'auto_created_from_alert') {
                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Alert Number:</span>
                        <a
                          href={`/alerts/${activity.metadata?.alertId}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                        >
                          {activity.metadata?.alertNumber || '-'}
                        </a>
                      </div>
                      {activity.metadata?.caseNumber && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Case Number:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata.caseNumber}</span>
                        </div>
                      )}
                      {activity.metadata?.timeLimitMinutes && (
                        <div className="text-sm text-gray-600">
                          Case auto-created after {activity.metadata.timeLimitMinutes} minutes without alert resolution
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for manually created from alert
                if (activity.activityType === 'created_from_alert') {
                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Alert Number:</span>
                        <a
                          href={`/alerts/${activity.metadata?.alertId}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                        >
                          {activity.metadata?.alertNumber || '-'}
                        </a>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Case Number:</span>
                        <span className="text-gray-900 ml-1">{activity.metadata?.caseNumber || '-'}</span>
                      </div>
                    </div>
                  );
                }

                // Check for Change created from Problem
                if (activity.activityType === 'created_from_problem') {
                  const formatPriority = (priority: string): string => {
                    if (!priority) return '-';
                    return priority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Change Number:</span>
                        <span className="text-gray-900 ml-1">{activity.metadata?.changeNumber || '-'}</span>
                      </div>
                      {activity.metadata?.shortDescription && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Short Description:</span>
                          <span className="text-gray-900 ml-1">
                            <RichTextDisplay content={activity.metadata.shortDescription} />
                          </span>
                        </div>
                      )}
                      {activity.metadata?.priority && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Priority:</span>
                          <span className="text-gray-900 ml-1">{formatPriority(activity.metadata.priority)}</span>
                        </div>
                      )}
                      <div className="text-sm text-blue-600 mt-1">
                        Change created from Problem:{' '}
                        <a
                          href={`/problems/${activity.metadata?.createdFromProblemId}`}
                          className="font-medium hover:underline"
                        >
                          {activity.metadata?.createdFromProblemNumber}
                        </a>
                      </div>
                    </div>
                  );
                }

                // Check for Change duplicated from another Change
                if (activity.activityType === 'duplicated') {
                  const formatPriority = (priority: string): string => {
                    if (!priority) return '-';
                    return priority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Change Number:</span>
                        <span className="text-gray-900 ml-1">{activity.metadata?.changeNumber || '-'}</span>
                      </div>
                      {activity.metadata?.shortDescription && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Short Description:</span>
                          <span className="text-gray-900 ml-1">
                            <RichTextDisplay content={activity.metadata.shortDescription} />
                          </span>
                        </div>
                      )}
                      {activity.metadata?.priority && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Priority:</span>
                          <span className="text-gray-900 ml-1">{formatPriority(activity.metadata.priority)}</span>
                        </div>
                      )}
                      <div className="text-sm text-blue-600 mt-1">
                        Change duplicated from:{' '}
                        <a
                          href={`/changes/${activity.metadata?.duplicatedFromChangeId}`}
                          className="font-medium hover:underline"
                        >
                          {activity.metadata?.duplicatedFromChangeNumber}
                        </a>
                      </div>
                    </div>
                  );
                }

                // Check for regular ticket creation (not from alert)
                if (activity.activityType === 'created') {
                  const formatPriority = (priority: string): string => {
                    if (!priority) return '-';
                    return priority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  };

                  const isDuplicated = activity.metadata?.duplicatedFromNumber;
                  const duplicatedFromId = activity.metadata?.duplicatedFromId;
                  const isFromIncident = activity.metadata?.createdFromIncidentNumber;
                  const createdFromIncidentId = activity.metadata?.createdFromIncidentId;
                  const isFromProblem = activity.metadata?.createdFromProblemNumber;
                  const createdFromProblemId = activity.metadata?.createdFromProblemId;

                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Ticket Number:</span>
                        <span className="text-gray-900 ml-1">{activity.metadata?.caseNumber || '-'}</span>
                      </div>
                      {activity.metadata?.shortDescription && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Short Description:</span>
                          <span className="text-gray-900 ml-1">
                            <RichTextDisplay content={activity.metadata.shortDescription} />
                          </span>
                        </div>
                      )}
                      {activity.metadata?.priority && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Priority:</span>
                          <span className="text-gray-900 ml-1">{formatPriority(activity.metadata.priority)}</span>
                        </div>
                      )}
                      {isDuplicated ? (
                        <div className="text-sm text-blue-600 mt-1">
                          Ticket created by duplicating the Incident -{' '}
                          <a
                            href={`/incidents/${duplicatedFromId}`}
                            className="font-medium hover:underline"
                          >
                            {activity.metadata.duplicatedFromNumber}
                          </a>
                        </div>
                      ) : isFromIncident ? (
                        <div className="text-sm text-blue-600 mt-1">
                          Ticket created successfully through Incident:{' '}
                          <a
                            href={`/incidents/${createdFromIncidentId}`}
                            className="font-medium hover:underline"
                          >
                            {activity.metadata.createdFromIncidentNumber}
                          </a>
                        </div>
                      ) : isFromProblem ? (
                        <div className="text-sm text-blue-600 mt-1">
                          Ticket created successfully through Problem:{' '}
                          <a
                            href={`/problems/${createdFromProblemId}`}
                            className="font-medium hover:underline"
                          >
                            {activity.metadata.createdFromProblemNumber}
                          </a>
                        </div>
                      ) : (
                        <div className="text-sm text-green-600 mt-1">
                          Ticket created successfully
                        </div>
                      )}
                    </div>
                  );
                }

                // Check for specific activity types
                // Note: attachment_added and attachment_removed are handled by detailed handlers below

                if (activity.activityType === 'servicenow_child_activity') {
                  const childLabel = activity.metadata?.childRecordType || 'ServiceNow record';
                  const childNumber = activity.metadata?.childNumber;
                  const summary = activity.metadata?.summary;
                  const requestNumber = activity.metadata?.requestNumber;
                  const changeNumber = activity.metadata?.changeNumber;
                  const parentNumber = requestNumber || changeNumber;
                  const parentLabel = requestNumber ? 'Request' : changeNumber ? 'Change' : null;
                  const field = activity.metadata?.field;
                  const previousValue = activity.metadata?.previousValue;
                  const newValue = activity.metadata?.newValue;
                  const journalValue = activity.metadata?.journalValue;

                  return (
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">
                          {childLabel}
                          {childNumber ? ` ${childNumber}` : ''}
                        </span>
                        {parentNumber && parentLabel && (
                          <span className="text-gray-500 ml-1">({parentLabel} {parentNumber})</span>
                        )}
                      </div>
                      {field && previousValue && newValue ? (
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium text-gray-700">{field}:</span>
                          <span className="text-gray-500 ml-1">{previousValue}</span>
                          <span className="text-gray-400 mx-1">&rarr;</span>
                          <span className="text-gray-900 font-medium">{newValue}</span>
                        </div>
                      ) : journalValue ? (
                        <div className="text-sm text-gray-600 mt-1">{journalValue}</div>
                      ) : summary ? (
                        <div className="text-sm text-gray-600 mt-1">{summary}</div>
                      ) : null}
                      <div className="text-xs text-gray-500 mt-1">Synced from ServiceNow</div>
                    </div>
                  );
                }

                if (activity.activityType === 'assignment_change') {
                  const previousName = activity.metadata?.previousAssignedToName;
                  const newName =
                    activity.metadata?.newAssignedToName ||
                    activity.metadata?.assignedToName;
                  const isSelfAssignment = activity.User?.id &&
                    (activity.metadata?.assignedToId === activity.User.id ||
                     activity.metadata?.newAssignedToId === activity.User.id);

                  if (previousName && newName) {
                    return (
                      <div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Assigned To:</span>
                          <span className="text-gray-500 ml-1">{previousName}</span>
                          <span className="text-gray-400 mx-1">&rarr;</span>
                          <span className="text-gray-900 font-medium">{newName}</span>
                          {isSelfAssignment && (
                            <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>
                          )}
                        </div>
                        {activity.metadata?.syncSource === 'servicenow' && (
                          <div className="text-xs text-gray-500 mt-1">Synced from ServiceNow</div>
                        )}
                      </div>
                    );
                  }

                  if (newName) {
                    return (
                      <div>
                        Assigned to {newName}
                        {isSelfAssignment && (
                          <span className="ml-1 text-blue-600 text-xs font-medium">(Self-assigned)</span>
                        )}
                      </div>
                    );
                  }
                }

                if (activity.activityType === 'priority_change' && activity.metadata?.previousPriority && activity.metadata?.newPriority) {
                  const oldPriority = activity.metadata.previousPriority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  const newPriority = activity.metadata.newPriority.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                  return <div>From {oldPriority} to {newPriority}</div>;
                }

                if (activity.activityType === 'ticket_type_conversion' && activity.metadata?.previousTicketType && activity.metadata?.newTicketType) {
                  return <div>From {activity.metadata.previousTicketType} to {activity.metadata.newTicketType}</div>;
                }

                // Attachment Uploaded - show filename, size, type, and preview for images
                if (activity.activityType === 'ATTACHMENT_UPLOADED' || activity.activityType === 'attachment_added') {
                  const formatFileSize = (bytes: number): string => {
                    if (bytes === 0) return '0 Bytes';
                    const k = 1024;
                    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                    const i = Math.floor(Math.log(bytes) / Math.log(k));
                    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                  };

                  const getFileIcon = (mimeType: string): string => {
                    if (mimeType?.startsWith('image/')) return '🖼️';
                    if (mimeType === 'application/pdf') return '📄';
                    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
                    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
                    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return '📦';
                    return '📎';
                  };

                  const mimeType = activity.metadata?.mimeType || '';
                  const filename = activity.metadata?.filename || '';
                  const canPreview = activity.metadata?.isImage ||
                    activity.metadata?.isPdf ||
                    isOfficeDocument(mimeType) ||
                    isCsvFile(mimeType, filename);

                  const handlePreviewClick = async () => {
                    if (activity.metadata?.storagePath || activity.metadata?.attachmentId) {
                      const isWord = isWordDocument(mimeType);
                      const isExcel = isExcelDocument(mimeType);
                      const isCsv = isCsvFile(mimeType, filename);
                      const attachmentId = activity.metadata?.attachmentId;

                      setPreviewFile({
                        filename: activity.metadata.filename || 'Unknown file',
                        mimeType: mimeType,
                        storagePath: activity.metadata.storagePath,
                        size: activity.metadata.size || 0,
                        isImage: activity.metadata.isImage || false,
                        isPdf: activity.metadata.isPdf || false,
                        isWord,
                        isExcel,
                        isCsv,
                        attachmentId
                      });

                      // For Word/Excel/CSV files, we need to fetch and parse the content
                      if (isWord || isExcel || isCsv) {
                        setPreviewLoading(true);
                        setPreviewContent(null);
                        setPreviewError(null);

                        try {
                          let blob: Blob;

                          // Try to use attachment ID with backend API first (works in local dev)
                          if (attachmentId) {
                            try {
                              const response = await api.get(
                                `/cases/${entityId}/attachments/${attachmentId}/download`,
                                { responseType: 'blob' }
                              );
                              blob = response.data;
                            } catch (apiError) {
                              // Fallback to direct fetch (for Azure storage with SAS URLs)
                              const response = await fetch(activity.metadata.storagePath);
                              blob = await response.blob();
                            }
                          } else {
                            // Direct fetch (for Azure storage with SAS URLs)
                            const response = await fetch(activity.metadata.storagePath);
                            blob = await response.blob();
                          }

                          if (isCsv) {
                            // Parse CSV
                            const text = await blob.text();
                            const workbook = XLSX.read(text, { type: 'string' });
                            let html = '';
                            workbook.SheetNames.forEach((sheetName) => {
                              const worksheet = workbook.Sheets[sheetName];
                              html += XLSX.utils.sheet_to_html(worksheet, { header: '', footer: '' });
                            });
                            setPreviewContent(html);
                          } else if (isWord) {
                            // Parse Word document
                            const arrayBuffer = await blob.arrayBuffer();
                            const result = await mammoth.convertToHtml({ arrayBuffer });
                            setPreviewContent(result.value);
                          } else if (isExcel) {
                            // Parse Excel spreadsheet
                            const arrayBuffer = await blob.arrayBuffer();
                            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                            let html = '';
                            workbook.SheetNames.forEach((sheetName) => {
                              const worksheet = workbook.Sheets[sheetName];
                              html += `<h3 style="margin: 20px 0 10px 0; color: #1f2937; font-size: 1.1rem; font-weight: 600;">${sheetName}</h3>`;
                              html += XLSX.utils.sheet_to_html(worksheet, { header: '', footer: '' });
                            });
                            setPreviewContent(html);
                          }
                        } catch (error) {
                          console.error('Failed to parse document:', error);
                          setPreviewError('Failed to load document preview');
                        } finally {
                          setPreviewLoading(false);
                        }
                      } else if (activity.metadata?.isImage || activity.metadata?.isPdf) {
                        // For images and PDFs, fetch via API and create blob URL
                        setPreviewLoading(true);
                        setPreviewUrl(null);
                        setPreviewError(null);

                        try {
                          let blob: Blob;

                          if (attachmentId) {
                            try {
                              const response = await api.get(
                                `/cases/${entityId}/attachments/${attachmentId}/download`,
                                { responseType: 'blob' }
                              );
                              blob = response.data;
                            } catch (apiError) {
                              // Fallback to direct fetch (for Azure storage with SAS URLs)
                              const response = await fetch(activity.metadata.storagePath);
                              blob = await response.blob();
                            }
                          } else {
                            const response = await fetch(activity.metadata.storagePath);
                            blob = await response.blob();
                          }

                          // Create blob URL with correct MIME type
                          const blobWithType = new Blob([blob], { type: mimeType });
                          const url = URL.createObjectURL(blobWithType);
                          setPreviewUrl(url);
                        } catch (error) {
                          console.error('Failed to load file:', error);
                          setPreviewError('Failed to load file preview');
                        } finally {
                          setPreviewLoading(false);
                        }
                      }
                    }
                  };

                  return (
                    <div className="space-y-2">
                      <div
                        className={`flex items-start space-x-3 ${canPreview ? 'cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors' : ''}`}
                        onClick={canPreview ? handlePreviewClick : undefined}
                        title={canPreview ? 'Click to preview' : undefined}
                      >
                        {/* File icon or image preview */}
                        <div className="flex-shrink-0 relative">
                          {activity.metadata?.isImage && activity.metadata?.storagePath ? (
                            <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 group">
                              <img
                                src={activity.metadata.storagePath}
                                alt={activity.metadata.filename}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  // nosec: innerHTML with static string - no user input, safe fallback UI
                                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<span class="flex items-center justify-center w-full h-full text-2xl">🖼️</span>';
                                }}
                              />
                              {canPreview && (
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-all">
                                  <ArrowTopRightOnSquareIcon className="w-6 h-6 text-white opacity-0 hover:opacity-100" />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-2xl ${canPreview ? 'hover:bg-blue-100' : ''}`}>
                              {getFileIcon(activity.metadata?.mimeType)}
                            </div>
                          )}
                        </div>

                        {/* File details */}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium text-gray-900 truncate ${canPreview ? 'text-blue-600 hover:text-blue-800 hover:underline' : ''}`}>
                            {activity.metadata?.filename || 'Unknown file'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            <div>
                              <span className="font-medium">Size:</span>
                              <span className="ml-1">{formatFileSize(activity.metadata?.size || 0)}</span>
                            </div>
                            <div>
                              <span className="font-medium">Type:</span>
                              <span className="ml-1">{activity.metadata?.mimeType || 'Unknown'}</span>
                            </div>
                          </div>
                          {canPreview && (
                            <div className="text-xs text-blue-500 mt-1">
                              Click to preview
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded inline-block">
                        ✓ File uploaded successfully
                      </div>
                    </div>
                  );
                }

                // Attachment Deleted - show filename and confirmation
                if (activity.activityType === 'ATTACHMENT_DELETED' || activity.activityType === 'attachment_removed') {
                  const formatFileSize = (bytes: number): string => {
                    if (bytes === 0) return '0 Bytes';
                    const k = 1024;
                    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                    const i = Math.floor(Math.log(bytes) / Math.log(k));
                    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                  };

                  return (
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center text-2xl">
                          🗑️
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate line-through">
                            {activity.metadata?.filename || 'Unknown file'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            <div>
                              <span className="font-medium">Size:</span>
                              <span className="ml-1">{formatFileSize(activity.metadata?.size || 0)}</span>
                            </div>
                            <div>
                              <span className="font-medium">Type:</span>
                              <span className="ml-1">{activity.metadata?.mimeType || 'Unknown'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded inline-block">
                        ✕ File deleted
                      </div>
                    </div>
                  );
                }

                // SLA Activity handlers
                if (activity.activityType?.startsWith('SLA_')) {
                  // Helper function to format duration
                  const formatDuration = (seconds: number): string => {
                    if (!seconds) return '-';
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    if (hours > 0) {
                      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
                    }
                    return `${minutes}m`;
                  };

                  // Helper function to format target type
                  const formatTargetType = (type: string): string => {
                    if (!type) return '-';
                    return type.charAt(0) + type.slice(1).toLowerCase();
                  };

                  // SLA Started - show policy name, target type, max time, target time
                  if (activity.activityType === 'SLA_STARTED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Max Time:</span>
                          <span className="text-gray-900 ml-1">{formatDuration(activity.metadata?.durationSeconds)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Target:</span>
                          <span className="text-gray-900 ml-1">
                            {activity.metadata?.targetTime ? formatDateTime(activity.metadata.targetTime) : '-'}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  // SLA Paused - show policy name
                  if (activity.activityType === 'SLA_PAUSED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-amber-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm text-amber-600">
                          SLA clock has been paused
                        </div>
                      </div>
                    );
                  }

                  // SLA Resumed - show policy name, new target time
                  if (activity.activityType === 'SLA_RESUMED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-green-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        {activity.metadata?.newTargetTime && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA New Target:</span>
                            <span className="text-gray-900 ml-1">
                              {formatDateTime(activity.metadata.newTargetTime)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-green-600">
                          SLA clock has been resumed
                        </div>
                      </div>
                    );
                  }

                  // SLA Reset - show policy name, new target time
                  if (activity.activityType === 'SLA_RESET') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-blue-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Max Time:</span>
                          <span className="text-gray-900 ml-1">{formatDuration(activity.metadata?.durationSeconds)}</span>
                        </div>
                        {activity.metadata?.newTargetTime && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA New Target:</span>
                            <span className="text-gray-900 ml-1">
                              {formatDateTime(activity.metadata.newTargetTime)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-blue-600">
                          SLA timer has been reset
                        </div>
                      </div>
                    );
                  }

                  // SLA Cancelled - show policy name
                  if (activity.activityType === 'SLA_CANCELLED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          SLA has been cancelled
                        </div>
                      </div>
                    );
                  }

                  // SLA Completed - show policy name, percentage elapsed, breach status
                  if (activity.activityType === 'SLA_COMPLETED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-green-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        {activity.metadata?.percentageElapsed !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Time Used:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.percentageElapsed.toFixed(1)}%</span>
                          </div>
                        )}
                        <div className={`text-sm font-medium ${activity.metadata?.breached ? 'text-red-600' : 'text-green-600'}`}>
                          {activity.metadata?.breached ? 'Completed after breach' : 'Completed within SLA'}
                        </div>
                      </div>
                    );
                  }

                  // SLA Breached - show policy name, target type, SLA started, max time, target time, breach time
                  if (activity.activityType === 'SLA_BREACHED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-red-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Time Limit:</span>
                          <span className="text-gray-900 ml-1">{formatDuration(activity.metadata?.durationSeconds)}</span>
                        </div>
                        {activity.metadata?.startedAt && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Started:</span>
                            <span className="text-gray-900 ml-1">
                              {formatDateTime(activity.metadata.startedAt)}
                            </span>
                          </div>
                        )}
                        {activity.metadata?.targetTime && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Target Time:</span>
                            <span className="text-gray-900 ml-1">
                              {formatDateTime(activity.metadata.targetTime)}
                            </span>
                          </div>
                        )}
                        {activity.metadata?.breachedAt && (
                          <div className="text-sm">
                            <span className="font-medium text-red-700">SLA Breached At:</span>
                            <span className="text-red-600 ml-1">
                              {formatDateTime(activity.metadata.breachedAt)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm font-medium text-red-600 mt-1 p-2 bg-red-50 rounded">
                          SLA has been breached
                        </div>
                      </div>
                    );
                  }

                  // SLA Warning - show policy name, warning level, percentage elapsed
                  if (activity.activityType === 'SLA_WARNING') {
                    const warningLevelColors: Record<string, string> = {
                      YELLOW: 'text-yellow-600',
                      ORANGE: 'text-orange-600',
                      RED: 'text-red-600'
                    };
                    const warningColor = warningLevelColors[activity.metadata?.warningLevel] || 'text-amber-600';
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Warning Level:</span>
                          <span className={`ml-1 font-medium ${warningColor}`}>
                            {activity.metadata?.warningLevel || 'Unknown'}
                          </span>
                        </div>
                        {activity.metadata?.percentageElapsed !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Time Elapsed:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.percentageElapsed.toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // SLA Reopened - show policy name, remaining time, reopened from state
                  if (activity.activityType === 'SLA_REOPENED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-blue-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        {activity.metadata?.reopenedFrom && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Reopened From:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.reopenedFrom}</span>
                          </div>
                        )}
                        {activity.metadata?.remainingSeconds !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Time Remaining:</span>
                            <span className="text-gray-900 ml-1">{formatDuration(activity.metadata.remainingSeconds)}</span>
                          </div>
                        )}
                        {activity.metadata?.newTargetTime && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA New Target:</span>
                            <span className="text-gray-900 ml-1">
                              {formatDateTime(activity.metadata.newTargetTime)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-blue-600">
                          SLA has been reopened
                        </div>
                      </div>
                    );
                  }

                  // SLA Completed due to Type Change - show policy name, conversion info, breach status
                  if (activity.activityType === 'SLA_COMPLETED_TYPE_CHANGE') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-purple-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Converted From:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.previousEntityType || '-'}</span>
                          <span className="text-gray-400 mx-1">→</span>
                          <span className="text-gray-900">{activity.metadata?.newEntityType || '-'}</span>
                        </div>
                        {activity.metadata?.percentageElapsed !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Progress at Completion:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.percentageElapsed.toFixed(1)}%</span>
                          </div>
                        )}
                        {activity.metadata?.previousState && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">SLA Previous State:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.previousState}</span>
                          </div>
                        )}
                        <div className={`text-sm font-medium mt-1 p-2 rounded ${activity.metadata?.wasBreached ? 'text-red-600 bg-red-50' : 'text-purple-600 bg-purple-50'}`}>
                          {activity.metadata?.wasBreached
                            ? 'SLA was already BREACHED before type conversion'
                            : 'SLA completed due to ticket type conversion'}
                        </div>
                      </div>
                    );
                  }

                  // SLA Discarded - show when SLA is discarded due to condition field changes
                  if (activity.activityType === 'SLA_DISCARDED') {
                    const fieldChanges = activity.metadata?.fieldChanges || {};
                    const fieldChangeEntries = Object.entries(fieldChanges);
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-slate-700">SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        {activity.metadata?.previousState && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Previous State:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.previousState}</span>
                          </div>
                        )}
                        {activity.metadata?.elapsedSeconds !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Time Elapsed:</span>
                            <span className="text-gray-900 ml-1">
                              {Math.floor(activity.metadata.elapsedSeconds / 3600)}h {Math.floor((activity.metadata.elapsedSeconds % 3600) / 60)}m {activity.metadata.elapsedSeconds % 60}s
                            </span>
                          </div>
                        )}
                        {activity.metadata?.percentageElapsed !== undefined && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Progress at Discard:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.percentageElapsed.toFixed(1)}%</span>
                          </div>
                        )}
                        {fieldChangeEntries.length > 0 && (
                          <div className="text-sm mt-2">
                            <span className="font-medium text-slate-700">Field Changes:</span>
                            <div className="mt-1 p-2 bg-slate-50 rounded space-y-1">
                              {fieldChangeEntries.map(([field, change]: [string, any]) => (
                                <div key={field} className="text-xs">
                                  <span className="font-medium text-slate-600">{field}:</span>
                                  <span className="text-slate-500 ml-1">{change.oldValue || 'empty'}</span>
                                  <span className="text-slate-400 mx-1">→</span>
                                  <span className="text-slate-700">{change.newValue || 'empty'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className={`text-sm font-medium mt-1 p-2 rounded ${activity.metadata?.wasBreached ? 'text-red-600 bg-red-50' : 'text-slate-600 bg-slate-100'}`}>
                          {activity.metadata?.wasBreached
                            ? 'SLA was BREACHED before being discarded'
                            : 'SLA discarded due to condition field changes - a new SLA may be attached'}
                        </div>
                      </div>
                    );
                  }

                  // SLA Re-evaluated - show when a new SLA is attached after condition field changes
                  if (activity.activityType === 'SLA_REEVALUATED') {
                    return (
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-emerald-700">New SLA Policy:</span>
                          <span className="text-gray-900 ml-1">{activity.metadata?.policyName || '-'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">SLA Type:</span>
                          <span className="text-gray-900 ml-1">{formatTargetType(activity.metadata?.targetType)}</span>
                        </div>
                        {activity.metadata?.reason && (
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Reason:</span>
                            <span className="text-gray-900 ml-1">{activity.metadata.reason}</span>
                          </div>
                        )}
                        <div className="text-sm font-medium mt-1 p-2 rounded text-emerald-600 bg-emerald-50">
                          New SLA attached after condition re-evaluation
                        </div>
                      </div>
                    );
                  }
                }

                // Check for work notes
                if ((activity.activityType === 'NOTE_ADDED_CUSTOMER' || activity.activityType === 'NOTE_ADDED_INTERNAL') && activity.metadata?.note) {
                  const noteColor = activity.activityType === 'NOTE_ADDED_CUSTOMER' ? 'text-blue-700' : 'text-gray-700';
                  return (
                    <div className={noteColor}>
                      <RichTextDisplay content={activity.metadata.note} className={noteColor} />
                    </div>
                  );
                }

                // Check for breach reason activities
                if (activity.activityType === 'response_breach_reason_added' ||
                    activity.activityType === 'ongoing_breach_reason_added' ||
                    activity.activityType === 'resolution_breach_reason_added') {
                  const breachReason = activity.metadata?.breachReason;
                  const targetType = activity.metadata?.targetType;
                  const typeLabel = targetType === 'RESPONSE' ? 'Response' :
                                   targetType === 'ONGOING' ? 'Ongoing' :
                                   targetType === 'RESOLUTION' ? 'Resolution' : targetType;

                  return (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium text-orange-700">SLA Type:</span>
                        <span className="text-gray-900 ml-1">{typeLabel}</span>
                      </div>
                      {breachReason && (
                        <div className="text-sm">
                          <span className="font-medium text-orange-700">Breach Reason:</span>
                          <div className="text-gray-900 mt-1 p-2 bg-orange-50 rounded">
                            <RichTextDisplay content={breachReason} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Fall back to metadata
                if (activity.metadata) {
                  if (activity.metadata.oldState) {
                    const oldState = activity.metadata.oldState.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    return <div>From {oldState}</div>;
                  }
                  if (activity.metadata.comments) {
                    return <div><RichTextDisplay content={activity.metadata.comments} /></div>;
                  }
                  if (activity.metadata.description) {
                    return <div><RichTextDisplay content={activity.metadata.description} /></div>;
                  }
                }
                return <div>-</div>;
              };

              return (
                <tr key={activity.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-40">
                    {formatDateTime(activity.performedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-48">
                    {getActivityDescription()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-40">
                    {activity.User ? `${activity.User.firstName || ''} ${activity.User.lastName || ''}`.trim() : 'System'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {renderActivityDetails()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="preview-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
              onClick={() => {
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setPreviewFile(null);
                setPreviewContent(null);
                setPreviewError(null);
                setPreviewUrl(null);
              }}
            />

            {/* Modal content */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">
                    {previewFile.isImage ? '🖼️' : previewFile.isPdf ? '📄' : previewFile.isWord ? '📝' : previewFile.isExcel || previewFile.isCsv ? '📊' : '📎'}
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 truncate max-w-md">
                      {previewFile.filename}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {previewFile.mimeType} • {(() => {
                        const bytes = previewFile.size;
                        if (bytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                      })()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                      }
                      setPreviewFile(null);
                      setPreviewContent(null);
                      setPreviewError(null);
                      setPreviewUrl(null);
                    }}
                    className="inline-flex items-center p-1.5 border border-transparent text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview content */}
              <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100 flex items-center justify-center">
                {/* Loading state for Word/Excel/CSV */}
                {previewLoading ? (
                  <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading preview...</p>
                  </div>
                ) : previewError ? (
                  <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
                    <span className="text-6xl mb-4">⚠️</span>
                    <p className="text-gray-500 mb-4">{previewError}</p>
                    {previewFile.attachmentId ? (
                      <button
                        onClick={async () => {
                          try {
                            const response = await api.get(
                              `/cases/${entityId}/attachments/${previewFile.attachmentId}/download`,
                              { responseType: 'blob' }
                            );
                            const url = URL.createObjectURL(response.data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = previewFile.filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Download failed:', error);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Download file instead
                      </button>
                    ) : (
                      <a
                        href={previewFile.storagePath}
                        download={previewFile.filename}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Download file instead
                      </a>
                    )}
                  </div>
                ) : previewFile.isImage && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement).parentElement;
                      if (parent) {
                        // nosec: innerHTML with static string - no user input, safe fallback UI for image load error
                        parent.innerHTML = `
                          <div class="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
                            <span class="text-6xl mb-4">🖼️</span>
                            <p class="text-gray-500">Unable to load image preview</p>
                          </div>
                        `;
                      }
                    }}
                  />
                ) : previewFile.isPdf && previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title={previewFile.filename}
                    className="w-full h-[70vh] bg-white rounded shadow-lg"
                  />
                ) : previewFile.isWord && previewContent ? (
                  // Word document viewer - styled like a document
                  <div className="w-full h-[70vh] overflow-auto bg-gray-100 p-8">
                    <div
                      className="max-w-4xl mx-auto bg-white shadow-lg p-12 rounded"
                      style={{
                        minHeight: '100%',
                        fontFamily: '"Calibri", "Arial", sans-serif',
                        fontSize: '11pt',
                        lineHeight: '1.6',
                        color: '#000000'
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }} />
                    </div>
                  </div>
                ) : (previewFile.isExcel || previewFile.isCsv) && previewContent ? (
                  // Excel/CSV spreadsheet viewer
                  <div className="w-full h-[70vh] overflow-auto bg-white p-4 rounded shadow-lg">
                    <style>{`
                      .preview-excel table {
                        border-collapse: collapse;
                        width: 100%;
                        font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                        font-size: 11px;
                        margin-bottom: 30px;
                      }
                      .preview-excel td, .preview-excel th {
                        border: 1px solid #d0d0d0;
                        padding: 4px 8px;
                        text-align: left;
                        background-color: white;
                        min-width: 60px;
                      }
                      .preview-excel th {
                        background-color: #f0f0f0;
                        font-weight: 600;
                      }
                      .preview-excel tr:hover td {
                        background-color: #f5f5f5;
                      }
                    `}</style>
                    <div
                      className="preview-excel"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
                    <span className="text-6xl mb-4">📎</span>
                    <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                    <a
                      href={previewFile.storagePath}
                      download={previewFile.filename}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Download file
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
